// Export PDF du journal de paie, reproduisant la mise en page du « Journal de Paie » Cegid PAIE-GRH :
// bandeau titre, en-tête de colonnes grisé, un bloc de 3 lignes par salarié, TOTAL GENERAL en fin de journal.
// Coordonnées en points, reprises de l'édition Cegid (A4 portrait) ; y = ligne de base du texte.

import { jsPDF } from 'jspdf';
import { frDate } from './journalPaieControle';

const X0 = 2;                                           // décalage horizontal vers le format A4
const COLS = [12, 176, 233, 312, 386, 446, 511, 576];   // bordures verticales des colonnes
const RIGHT = { heures: 230.2, ret: 443.2, assujet: 508.4, divers: 573.6 }; // bords d'alignement à droite
const MATRICULE_RIGHT = 72.6;
const BLOCK_H = 42;
const PER_PAGE_FIRST = 15;
const PER_PAGE = 16;
const GRAY_HEADER = [205, 205, 205];
const GRAY_TITLE = [160, 160, 160];
const PAR_DEFAUT = 'R.AMGAAD'; // signataire « Par … » du pied de page

// Montant au format Cegid : « 7 498,70 » (espace normale — l'espace fine de toLocaleString n'existe pas en Helvetica)
const num = (n) => {
  const v = Number(n) || 0;
  const [int, dec] = Math.abs(v).toFixed(2).split('.');
  return `${v < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`;
};
const numIf = (n) => (Number(n) ? num(n) : '');

const pad = (n) => String(n).padStart(2, '0');

function drawHeader(doc, journal, first, matFrom, matTo) {
  const dy = first ? 0 : -51.8;
  const x = (v) => v + X0;

  if (first) {
    doc.setFillColor(...GRAY_TITLE);
    doc.setDrawColor(90);
    doc.setLineWidth(0.6);
    doc.rect(x(12), 12, COLS[7] - 12, 28, 'FD');
    doc.setFont('helvetica', 'bold').setFontSize(16).setTextColor(255);
    doc.text('Journal de Paie', x(297), 31.5, { align: 'center' });
    doc.setTextColor(0);
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(8);
    doc.text('Journal de Paie', x(298), 13.2, { align: 'center' });
    doc.setDrawColor(0).setLineWidth(0.5);
    doc.line(x(12), 19, x(COLS[7]), 19);
  }

  // Période / établissement / plage de matricules
  doc.setFont('helvetica', 'normal').setFontSize(8);
  const yPer = first ? 50.7 : 32.7;
  doc.text('Période de', x(32.9), yPer);
  doc.text(frDate(journal.periode_debut), x(102.1), yPer);
  doc.text('à', x(148.4), yPer);
  doc.text(frDate(journal.periode_fin), x(159.1), yPer);
  if (first) {
    doc.text('Etablissement(s)', x(32.9), 66.5);
    doc.text(journal.etablissement && journal.etablissement !== 'Tous' ? journal.etablissement : '<<Tous>>', x(98.2), 66.5);
    doc.text('Salarié de', x(32.9), 82.2);
    doc.text(String(matFrom || ''), x(111.1), 82.2);
    doc.text('à', x(154.4), 82.2);
    doc.text(String(matTo || ''), x(176.3), 82.2);
    doc.setFont('helvetica', 'italic');
    doc.text('Devises', x(425.2), 82.2);
    doc.text('DH', x(479.2), 82.2);
  }

  // En-tête de colonnes
  const top = 92 + dy;
  doc.setFillColor(...GRAY_HEADER);
  doc.setDrawColor(60).setLineWidth(0.6);
  doc.roundedRect(x(COLS[0]), top, COLS[7] - COLS[0], 39, 4, 4, 'FD');
  doc.setLineWidth(0.4);
  for (const c of COLS.slice(1, -1)) doc.line(x(c), top, x(c), top + 39);

  doc.setFont('helvetica', 'bold').setFontSize(8);
  const labels = [
    ['Matricule', 23.9, 101], ['Nom  Prénom', 23.9, 113], ["Date d'entrée", 23.9, 125], ['Date de Sortie', 98.2, 125],
    ['Heures', 187.3, 101], ['Brut', 192.7, 113], ['Brut Abattu', 179, 125],
    ['Tranche 1', 251, 101.8], ['Tranche 2', 251, 112.2], ['Tranche 3', 251, 123.5],
    ['Base SS', 333.2, 101], ['Base CSG', 329.9, 112.2], ['Base CRDS', 327.3, 125.8],
    ['Ret. Sal.', 401, 101], ['Ret. Pat.', 400.8, 119.8],
    ['Assujet.', 465.2, 101], ['Net Imposable', 453.1, 119.8],
    ['Divers plus', 523.5, 101], ['Divers moins', 519.9, 113], ['Net a Payer', 523.5, 125],
  ];
  for (const [t, lx, ly] of labels) doc.text(t, x(lx), ly + dy);
  doc.setFont('helvetica', 'normal');
  return top + 39 + 3; // haut du premier bloc salarié
}

function drawLigne(doc, l, top) {
  const x = (v) => v + X0;
  const r0 = top + 9;    // ligne matricule / heures / ret. sal. / assujet. / divers plus
  const r1 = top + 20.5; // ligne nom / brut / divers moins
  const r2 = top + 32.5; // ligne dates / brut abattu / net à payer
  const rm = top + 27;   // ret. pat. / net imposable (centrés)

  doc.setDrawColor(60).setLineWidth(0.5);
  doc.rect(x(COLS[0]), top, COLS[7] - COLS[0], BLOCK_H);
  doc.setLineWidth(0.4);
  for (const c of COLS.slice(1, -1)) doc.line(x(c), top, x(c), top + BLOCK_H);

  doc.setFont('helvetica', 'normal').setFontSize(8);
  doc.text(String(l.matricule || ''), x(MATRICULE_RIGHT), r0, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  const nom = `${l.nom || ''} .`;
  doc.text(doc.splitTextToSize(nom, COLS[1] - 23.9 - 2)[0], x(23.9), r1);
  doc.setFont('helvetica', 'normal');
  if (l.date_entree) doc.text(frDate(l.date_entree), x(23.9), r2);
  if (l.date_sortie) doc.text(frDate(l.date_sortie), x(102.6), r2);

  const right = (v, col, y) => { if (v !== '') doc.text(v, x(RIGHT[col]), y, { align: 'right' }); };
  right(numIf(l.heures), 'heures', r0);
  right(numIf(l.brut), 'heures', r1);
  right(numIf(l.brut_abattu), 'heures', r2);
  right(numIf(l.ret_sal), 'ret', r0);
  right(num(l.ret_pat), 'ret', rm);
  right(numIf(l.assujet), 'assujet', r0);
  right(num(l.net_imposable), 'assujet', rm);
  right(numIf(l.divers_plus), 'divers', r0);
  right(numIf(l.divers_moins), 'divers', r1);
  right(num(l.net_a_payer), 'divers', r2);
}

function drawTotal(doc, totals, nb, top) {
  const x = (v) => v + X0;
  const h = 32;
  doc.setFillColor(...GRAY_HEADER);
  doc.setDrawColor(60).setLineWidth(0.6);
  doc.roundedRect(x(COLS[0]), top, COLS[7] - COLS[0], h, 4, 4, 'FD');
  doc.setLineWidth(0.4);
  for (const c of COLS.slice(1, -1)) doc.line(x(c), top, x(c), top + h);

  doc.setFont('helvetica', 'bold').setFontSize(8);
  const r0 = top + 7.5;
  const r1 = top + 17.2;
  const r2 = top + 28.5;
  const rm = top + 24;
  doc.text('Nombre de Salarié(s) Traité(s) :', x(20.2), r0);
  doc.text(String(nb), x(161.1), r0, { align: 'right' });
  doc.text('TOTAL GENERAL', x(96.5), rm, { align: 'center' });

  const right = (v, col, y) => { if (v !== '') doc.text(v, x(RIGHT[col]), y, { align: 'right' }); };
  right(numIf(totals.heures), 'heures', r0);
  right(num(totals.brut), 'heures', r1);
  right(num(totals.brut_abattu), 'heures', r2);
  right(num(totals.ret_sal), 'ret', r0);
  right(num(totals.ret_pat), 'ret', rm);
  right(numIf(totals.assujet), 'assujet', r0);
  right(num(totals.net_imposable), 'assujet', rm);
  right(num(totals.divers_plus), 'divers', r0);
  right(num(totals.divers_moins), 'divers', r1);
  right(num(totals.net_a_payer), 'divers', r2);
  doc.setFont('helvetica', 'normal');
}

function drawFooter(doc, journal, pageNo, imprimeLe, par) {
  const x = (v) => v + X0;
  const H = doc.internal.pageSize.getHeight();
  doc.setDrawColor(0).setLineWidth(0.5);
  doc.line(x(12), H - 40, x(COLS[7]), H - 40);
  doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(0);
  doc.text(`Imprimé le ${imprimeLe}`, x(17.9), H - 29);
  if (par) doc.text(`Par ${par}`, x(17.9), H - 17);
  doc.text('© Copyright Yourcegid - PAIE-GRH', x(297), H - 29, { align: 'center' });
  doc.text('Version 11.0.2 du 05/10/2016', x(297), H - 17, { align: 'center' });
  doc.text(`Page n° ${pageNo}`, x(COLS[7] - 1), H - 29, { align: 'right' });
  if (journal.societe) doc.text(journal.societe, x(COLS[7] - 1), H - 17, { align: 'right' });
}

const sumCol = (lignes, k) => Math.round(lignes.reduce((s, l) => s + (Number(l[k]) || 0), 0) * 100) / 100;

export function buildJournalPdf(journal, { par = PAR_DEFAUT } = {}) {
  const lignes = journal.lignes || [];
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const now = new Date();
  const imprimeLe = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${String(now.getFullYear()).slice(2)} à ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const matFrom = lignes[0]?.matricule;
  const matTo = lignes[lignes.length - 1]?.matricule;

  // Découpage : 15 salariés en page 1, 16 sur les suivantes (comme l'édition Cegid)
  const pages = [];
  let i = 0;
  while (i < lignes.length || pages.length === 0) {
    const n = pages.length === 0 ? PER_PAGE_FIRST : PER_PAGE;
    pages.push(lignes.slice(i, i + n));
    i += n;
  }

  // Totaux : ceux imprimés sur le journal Cegid s'ils existent, sinon recalculés
  const keys = ['heures', 'brut', 'brut_abattu', 'ret_sal', 'ret_pat', 'assujet', 'net_imposable', 'divers_plus', 'divers_moins', 'net_a_payer'];
  const totals = journal.totaux && journal.totaux.net_a_payer != null
    ? journal.totaux
    : Object.fromEntries(keys.map((k) => [k, sumCol(lignes, k)]));
  const nb = journal.totaux?.nb_salaries ?? lignes.length;

  const H = doc.internal.pageSize.getHeight();
  pages.forEach((chunk, p) => {
    if (p > 0) doc.addPage();
    let top = drawHeader(doc, journal, p === 0, matFrom, matTo);
    chunk.forEach((l) => { drawLigne(doc, l, top); top += BLOCK_H; });
    if (p === pages.length - 1) {
      if (top + 8 + 32 > H - 44) { // pas la place pour le total : nouvelle page
        drawFooter(doc, journal, p + 1, imprimeLe, par);
        doc.addPage();
        top = drawHeader(doc, journal, false, matFrom, matTo);
        drawTotal(doc, totals, nb, top);
        drawFooter(doc, journal, p + 2, imprimeLe, par);
        return;
      }
      drawTotal(doc, totals, nb, top + 8);
    }
    drawFooter(doc, journal, p + 1, imprimeLe, par);
  });

  return doc;
}

export function exportJournalPdf(journal, opts) {
  const doc = buildJournalPdf(journal, opts);
  const soc = String(journal.societe || 'societe').replace(/[^a-z0-9]+/gi, '_');
  doc.save(`journal_paie_${soc}_${(journal.periode_debut || '').slice(0, 7)}.pdf`);
}
